import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { getFirebaseAuth } from '@services/firebase';
import { Console, removeUndefineds } from '@utils';
import { useRouter } from 'next/router';
import { getRoute } from '@utils/routes';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  currentUserInfo,
  CurrentUserInfoResponse,
} from '@queries/user/queries';
import { User } from '@interfaces';
import { GlobalApolloClient } from '@config/apollo';
import { useLoggedReducer } from './useLoggedReducer';
import { useTagManagerEvent } from './useTagManagerEvent';
import { onIdTokenChanged, User as FirebaseUser } from 'firebase/auth';
import { useSegment } from './useSegment';
import { getUTM } from '@utils/getUTM';
import {
  ValidateAuthMailMutation,
  ValidateAuthMailParams,
  ValidateAuthMailMutationResponse,
} from '@queries/authMail/mutations/validateAuthMail';
import { signInWithCustomToken } from 'firebase/auth';
import { getSentry } from '@services/sentry';
import nookies from 'nookies';
import { FIREBASE_API_KEY } from '@config/environment';
import { clearPersistedStates, usePersistState } from './usePersistState';
import { useEventTracking } from './useEventTracking';
import { AutomaticEventTypes } from '@validators/Events/Automatic';

declare global {
  interface Window {
    userId?: string;
  }
}
interface AuthState {
  firebaseUser?: FirebaseUser;
  appUser?: User;
  loading: boolean;
  queryLoading: boolean;
  firebaseLoading: boolean;
  tokenLoading: boolean;
  logout: () => Promise<void>;
  emailByToken: string | null;
  setEmailByToken: (email: string | null) => void;
}

const AuthStateContext = createContext<AuthState>({
  firebaseUser: null,
  appUser: null,
  loading: true,
  queryLoading: true,
  firebaseLoading: true,
  tokenLoading: false,
  logout: () => Promise.resolve(),
  emailByToken: null,
  setEmailByToken: () => {},
});

interface UseAuthParams {
  forceAuth?: boolean;
  forceUnauth?: boolean;
}
interface UseAuthResponse {
  firebaseUser?: FirebaseUser;
  appUser?: User;
  loading: boolean;
  queryLoading: boolean;
  firebaseLoading: boolean;
  logout: () => Promise<void>;
  emailByToken: string | null;
  setEmailByToken: (email: string | null) => void;
  tokenLoading: boolean;
}
export function useAuth({
  forceAuth,
  forceUnauth,
}: UseAuthParams = {}): UseAuthResponse {
  const { replace, route, asPath, query, isReady } = useRouter();

  const {
    firebaseLoading,
    queryLoading,
    appUser,
    firebaseUser,
    loading,
    logout,
    emailByToken,
    setEmailByToken,
    tokenLoading,
  } = useContext(AuthStateContext);
  const token = query?.token;
  useEffect(() => {
    if (loading) return;
    if (forceAuth && !appUser) {
      Console.log({
        msg: '[USE_AUTH] redirect: force auth and no user',
        forceAuth,
        appUser,
        route,
        isReady,
        token,
      });
      if (!isReady) return;
      if (!token) {
        void replace(getRoute('signin', { redirect: asPath ?? '/' }));
      }
    }
    if (forceUnauth && appUser) {
      Console.log({
        msg: '[USE_AUTH] redirect: force unauth and user',
        forceAuth,
        appUser,
        route,
      });
      void replace(getRoute('home'));
    }
  }, [
    loading,
    appUser,
    forceAuth,
    forceUnauth,
    replace,
    route,
    asPath,
    isReady,
    token,
  ]);

  return {
    firebaseLoading,
    queryLoading,
    appUser,
    firebaseUser,
    loading,
    logout,
    emailByToken,
    setEmailByToken,
    tokenLoading,
  };
}
type AuthAction =
  | { type: 'init_firebase_auth' }
  | { type: 'finish_firebase_auth'; payload: { firebaseUser: FirebaseUser } }
  | { type: 'init_app_auth' }
  | { type: 'finish_app_auth'; payload: { appUser: User } }
  | { type: 'update_app_auth'; payload: { appUser: User } }
  | { type: 'set_state'; payload: Partial<AuthState> }
  | { type: 'logout' }
  | { type: 'init_token_auth' }
  | { type: 'finish_token_auth' };
function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'init_firebase_auth': {
      return {
        ...state,
        firebaseUser: null,
        firebaseLoading: true,
        loading: true,
      };
    }
    case 'finish_firebase_auth': {
      return {
        ...state,
        firebaseUser: action.payload.firebaseUser,
        firebaseLoading: false,
        loading: Boolean(action.payload.firebaseUser),
      };
    }
    case 'init_app_auth': {
      return {
        ...state,
        appUser: null,
        queryLoading: true,
        loading: true,
      };
    }
    case 'finish_app_auth': {
      return {
        ...state,
        appUser: action.payload.appUser,
        queryLoading: false,
        loading: false,
      };
    }
    case 'update_app_auth': {
      return {
        ...state,
        appUser: action.payload.appUser,
      };
    }
    case 'set_state': {
      return { ...state, ...action.payload };
    }
    case 'init_token_auth': {
      return {
        ...state,
        loading: true,
        tokenLoading: true,
      };
    }
    case 'finish_token_auth': {
      return {
        ...state,
        loading: false,
        tokenLoading: false,
      };
    }
    case 'logout': {
      return {
        ...state,
        firebaseUser: null,
        appUser: null,
        loading: false,
        queryLoading: false,
        firebaseLoading: false,
      };
    }
    default: {
      return state;
    }
  }
}
interface AuthStateProviderProps {
  children: React.ReactNode;
}
export function AuthStateProvider({ children }: AuthStateProviderProps) {
  const { addCustomEvent } = useTagManagerEvent();
  const { reset, debugTrack } = useSegment();
  const { query, isReady, replace, pathname, asPath } = useRouter();
  const { track, identify } = useEventTracking();
  const [
    {
      firebaseUser,
      firebaseLoading,
      appUser,
      queryLoading,
      loading,
      logout,
      tokenLoading,
    },
    dispatch,
  ] = useReducer(useLoggedReducer(reducer), {
    firebaseUser: null,
    appUser: null,
    loading: true,
    queryLoading: true,
    firebaseLoading: true,
    tokenLoading: false,
    logout: () => Promise.resolve(),
    emailByToken: null,
    setEmailByToken: () => {},
  });

  const [executeQuery, { data, previousData }] =
    useLazyQuery<CurrentUserInfoResponse>(
      currentUserInfo('AuthStateProvider'),
      {
        fetchPolicy: 'network-only',
      },
    );

  const [validateAuthCode] = useMutation<
    ValidateAuthMailMutationResponse,
    ValidateAuthMailParams
  >(ValidateAuthMailMutation());

  const execAppLogin = useCallback(async () => {
    try {
      clearPersistedStates('screen_instance_id');
      const response = await executeQuery();
      Console.log({
        msg: '[AuthStateProvider] success app query',
        response,
      });
      dispatch({
        type: 'finish_app_auth',
        payload: { appUser: response.data.currentUserInfo },
      });
    } catch {
      Console.log({
        msg: '[AuthStateProvider]error app query',
      });
      dispatch({
        type: 'finish_app_auth',
        payload: { appUser: null },
      });
    }
  }, [executeQuery]);
  const firebaseUserRef = useRef(firebaseUser);
  const appUserRef = useRef(appUser);
  const [emailByToken, setEmailByToken] = usePersistState<string>(
    'emailByToken',
    '',
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFirebaseUser = useMemo(
    () => firebaseUser,
    [firebaseUser?.email],
  );

  const saveToken = useCallback((firebaseUserResponse: FirebaseUser) => {
    const refreshToken = firebaseUserResponse.refreshToken;
    const cookieExpiration = new Date(+new Date() + 1000 * 60 * 60 * 24 * 30);
    nookies.set(undefined, 'token', refreshToken, {
      path: '/',
      expires: cookieExpiration,
    });
  }, []);

  useEffect(() => {
    const onIdTokenChange = () => {
      return onIdTokenChanged(getFirebaseAuth(), (firebaseUserResponse) => {
        dispatch({ type: 'init_firebase_auth' });
        Console.log({
          msg: '[AuthStateProvider] onComplete firebase query',
          firebaseUserResponse,
        });
        dispatch({
          type: 'finish_firebase_auth',
          payload: { firebaseUser: firebaseUserResponse },
        });
        if (firebaseUserResponse) {
          dispatch({ type: 'init_app_auth' });
          void execAppLogin();
        } else {
          nookies.set(undefined, 'token', '', { path: '/' });
        }
      });
    };
    const unsubscribe = onIdTokenChange();
    return () => {
      unsubscribe();
    };
  }, [execAppLogin, saveToken]);
  useEffect(
    function appAuth() {
      if (loading) return;
      if (!firebaseUser) return;
      if (appUser) return;
      void execAppLogin();
      void saveToken(firebaseUser);
    },
    [execAppLogin, loading, appUser, firebaseUser, saveToken],
  );

  useEffect(() => {
    const handle = setInterval(() => {
      const user = firebaseUserRef.current;
      void executeQuery();
      if (user) void saveToken(user);
    }, 10 * 60 * 1000);
    // clean up setInterval
    return () => clearInterval(handle);
  }, [saveToken, executeQuery]);

  useEffect(
    function handleRefetch() {
      if ((firebaseUser && !appUser) || (!firebaseUser && appUser)) {
        Console.log('[USE_AUTH] auth change refetch', {
          firebaseUser,
          appUser: appUserRef.current,
        });
        void execAppLogin();
      }
    },
    [appUser, firebaseUser, execAppLogin],
  );

  useEffect(
    function updateFirebaseUserRef() {
      Console.log('[USE_AUTH] firebaseUser updated', {
        prevFirebaseUser: firebaseUserRef.current,
        firebaseUser: memoizedFirebaseUser,
      });
      firebaseUserRef.current = memoizedFirebaseUser;
    },
    [memoizedFirebaseUser],
  );
  useEffect(
    function updateAppUserRef() {
      Console.log('[USE_AUTH] appUser updated', {
        prevAppUser: appUserRef.current,
        appUser,
      });
      appUserRef.current = appUser;
    },
    [appUser],
  );
  useEffect(
    function handleAuthChangeEvent() {
      const prev = window.userId;
      window.userId = appUser?.uuid;
      if (appUser?.uuid) {
        addCustomEvent('authComplete', {
          userID: appUser.uuid,
          email: appUser.email,
          name: `${appUser.name} ${appUser.lastNames}`,
        });
        identify(appUser.uuid, {
          uuid: appUser.uuid,
          name: `${appUser.name} ${appUser.lastNames}`,
          email: appUser.email,
          birthdate: appUser.birthdate,
          has_insurance: appUser.hasUserInsurance,
          ...getUTM(),
        });
        return;
      }
      if (prev && !appUser?.uuid) {
        reset();
        addCustomEvent('signoutComplete', {
          userID: null,
          email: null,
          name: null,
        });
      }
    },
    [
      addCustomEvent,
      appUser?.email,
      appUser?.lastNames,
      appUser?.name,
      appUser?.uuid,
      identify,
      track,
    ],
  );
  useEffect(
    function setLogoutFunction() {
      dispatch({
        type: 'set_state',
        payload: {
          logout: async () => {
            Console.log('[USE_AUTH] logout function');
            await GlobalApolloClient.logout();
            await getFirebaseAuth().signOut();
            void track(AutomaticEventTypes.SessionLogout);
            dispatch({ type: 'logout' });
            clearPersistedStates('screen_instance_id');
          },
        },
      });
    },
    [addCustomEvent],
  );
  useEffect(
    function listenToApolloUserCache() {
      if (!previousData?.currentUserInfo && data?.currentUserInfo) {
        Console.log({
          msg: '[AuthStateProvider] listenToApolloUserCache',
          previousData,
          data,
        });
        dispatch({
          type: 'finish_app_auth',
          payload: { appUser: data.currentUserInfo },
        });
      } else if (data?.currentUserInfo) {
        Console.log({
          msg: '[AuthStateProvider] listenToApolloUserCache update user',
          previousData,
          data,
        });
        dispatch({
          type: 'update_app_auth',
          payload: { appUser: data.currentUserInfo },
        });
      }
    },
    [data, previousData],
  );
  useEffect(
    function authWithLinkToken() {
      Console.log({
        msg: 'authWithLinkToken',
        query: query.token,
        isReady,
        loading,
      });
      if (!isReady) return;
      if (loading) return;
      if (firebaseUser) return;
      if (appUser) return;
      if (query?.token) {
        dispatch({ type: 'init_token_auth' });
        Console.log({
          msg: '[USE_AUTH] authWithLinkToken I have a token going to validate',
          token: query.token,
        });
        const fetchResponse = async () => {
          const { data: authMailData } = await validateAuthCode({
            variables: {
              params: {
                token: query.token as string,
              },
            },
          });

          if (authMailData.validateAuthMail.isValid) {
            const authToken = authMailData.validateAuthMail.token;
            try {
              setEmailByToken(authMailData.validateAuthMail.email);
              await signInWithCustomToken(getFirebaseAuth(), authToken);
              Console.log({
                msg: '[USE_AUTH] authWithLinkToken: inicie session',
              });
            } catch (error) {
              Console.log({
                msg: "[USE_AUTH] authWithLinkToken Firebase User couldn't sign in with email link",
              });
              await getSentry().then((Sentry) => {
                Sentry.captureException(error);
              });
              const routerToken = (query?.token as string).replace(/\s.*/g, '');
              await replace(
                {
                  pathname: pathname,
                  query: removeUndefineds({
                    ...query,
                    token: routerToken,
                  }),
                },
                {
                  pathname: asPath.replace(/\?.*/, ''),
                  query: removeUndefineds({
                    ...query,
                    token: routerToken,
                  }),
                },
              );
            }
          } else {
            Console.log('[USE_AUTH] authWithLinkToken no valid token');
            const routerToken = (query?.token as string).replace(/\s.*/g, '');
            await replace(
              {
                pathname: pathname,
                query: removeUndefineds({
                  ...query,
                  token: routerToken,
                }),
              },
              {
                pathname: asPath.replace(/\?.*/, ''),
                query: removeUndefineds({
                  ...query,
                  token: routerToken,
                }),
              },
            );
          }
          dispatch({ type: 'finish_token_auth' });
        };
        void fetchResponse();
      }
    },
    [
      isReady,
      validateAuthCode,
      asPath,
      pathname,
      query,
      replace,
      setEmailByToken,
      loading,
      firebaseUser,
      appUser,
    ],
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const openRequest = indexedDB.open('firebaseLocalStorageDb');
    openRequest.onupgradeneeded = function (event) {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('firebaseLocalStorage')) {
        db.createObjectStore('firebaseLocalStorage');
      }
    };
    openRequest.onsuccess = function (event) {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction(['firebaseLocalStorage'], 'readonly');
      const store = transaction.objectStore('firebaseLocalStorage');
      const request = store.get(
        `firebase:authUser:${FIREBASE_API_KEY}:[DEFAULT]`,
      );
      request.onsuccess = function () {
        // Si la operación es exitosa, hacer log del valor de 'Key'.
        debugTrack('Firebase', 'Data from indexedDB', request.result);
      };

      request.onerror = function () {};
    };

    openRequest.onerror = function () {};
  }, [debugTrack]);
  const authState = useMemo(
    () => ({
      firebaseUser: memoizedFirebaseUser,
      loading: loading,
      queryLoading,
      firebaseLoading,
      appUser,
      logout,
      emailByToken,
      setEmailByToken,
      tokenLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(appUser),
      firebaseLoading,
      loading,
      logout,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(memoizedFirebaseUser),
      queryLoading,
      tokenLoading,
    ],
  );

  return (
    <AuthStateContext.Provider value={authState}>
      {children}
    </AuthStateContext.Provider>
  );
}
